import { AuthBaseClient } from '../base-client';
import type { SearchResponse } from '@vagusx/shared';
import type {
  EntityResponse,
  EntityContactResponse,
  CreateEntityRequest,
  UpdateEntityRequest,
  CreateEntityContactRequest,
  UpdateEntityContactRequest,
} from './types';

/**
 * Client for entity API endpoints.
 */
export class EntityClient extends AuthBaseClient {
  /**
   * Search entities.
   */
  async search(params?: {
    type?: string;
    searchTerm?: string;
    limit?: number;
    offset?: number;
  }): Promise<SearchResponse<EntityResponse>> {
    const query = new URLSearchParams();
    if (params?.type) query.set('type', params.type);
    if (params?.searchTerm) query.set('searchTerm', params.searchTerm);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.get<SearchResponse<EntityResponse>>(`/api/entities${qs ? `?${qs}` : ''}`);
  }

  /**
   * Get entity by ID.
   */
  async getById(id: string): Promise<EntityResponse> {
    return this.get<EntityResponse>(`/api/entities/${id}`);
  }

  /**
   * Create entity.
   */
  async create(data: CreateEntityRequest): Promise<EntityResponse> {
    return this.post<EntityResponse>('/api/entities', data);
  }

  /**
   * Update entity.
   */
  async update(id: string, data: UpdateEntityRequest): Promise<EntityResponse> {
    return this.patch<EntityResponse>(`/api/entities/${id}`, data);
  }

  /**
   * Delete entity.
   */
  async deleteEntity(id: string): Promise<void> {
    return this.delete<void>(`/api/entities/${id}`);
  }

  /**
   * Get contacts for an entity.
   */
  async getContacts(entityId: string): Promise<EntityContactResponse[]> {
    return this.get<EntityContactResponse[]>(`/api/entities/${entityId}/contacts`);
  }

  /**
   * Create contact for entity.
   */
  async createContact(
    entityId: string,
    data: CreateEntityContactRequest
  ): Promise<EntityContactResponse> {
    return this.post<EntityContactResponse>(`/api/entities/${entityId}/contacts`, data);
  }

  /**
   * Update entity contact.
   */
  async updateContact(
    entityId: string,
    contactId: string,
    data: UpdateEntityContactRequest
  ): Promise<EntityContactResponse> {
    return this.patch<EntityContactResponse>(
      `/api/entities/${entityId}/contacts/${contactId}`,
      data
    );
  }

  /**
   * Delete entity contact.
   */
  async deleteContact(entityId: string, contactId: string): Promise<void> {
    return this.delete<void>(`/api/entities/${entityId}/contacts/${contactId}`);
  }
}
